import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
// import TableCell from '@mui/material/TableCell';
import TableCell from 'components/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Switch from '@mui/material/Switch';
import Collapse from '@mui/material/Collapse';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonCurrencyInput from 'components/ArgonCurrencyInput';
import ArgonDatePicker from 'components/ArgonDatePicker';
import FormikNumberInput from 'components/FormikNumberInput';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StripeTaxRateSelector from 'Routes/Organizer/Organization/Settings/Payments/StripeTaxRates/Selector';
import { getSystemFeeForProductType } from 'services/REST_API/orders';
import CustomRequiredFields from './CustomRequiredFields';
import Swal from 'sweetalert2';
import SingleAdaptivePricingDialog from 'Routes/Organizer/Events/Details/EventInstances/EventInstanceDetails/components/AdaptivePricing/SingleAdaptivePricingDialog';
import ArgonButton from 'components/ArgonButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { Tooltip } from '@mui/material';

const EventInstancePricing = React.memo(
  ({
    formik,
    event,
    paymentsEnabled,
    paymentsEnabledLoading,
    formMinWidth
  }) => {
    const { t, i18n } = useTranslation();

    const [calculatedSystemFee, setCalculatedSystemFee] = useState(0);

    const [openAdaptivePricingDialog, setOpenAdaptivePricingDialog] =
      useState(false);
    const [editingPricePoint, setEditingPricePoint] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const handleOpenAdaptivePricingDialog = (existingData = null) => {
      setEditingPricePoint(existingData);
      setIsEditMode(!!existingData);
      setOpenAdaptivePricingDialog(true);
    };

    const handleCloseAdaptivePricingDialog = () => {
      setOpenAdaptivePricingDialog(false);
      setEditingPricePoint(null);
      setIsEditMode(false);
    };

    const handleSaveAdaptivePricing = (newData, originalData) => {
      console.log('handleSaveAdaptivePricing', { newData, originalData });

      const currentData = formik.values?.adaptiveRegistrationFeeData || [];
      let updatedData;

      if (isEditMode && originalData) {
        // Update existing entry using index-based identification
        const targetIndex = currentData.findIndex(
          (item, index) =>
            // Use multiple criteria to reliably identify the target item
            item.fromRegistrations === originalData.fromRegistrations &&
            item.fromDate === originalData.fromDate &&
            item.registrationFee === originalData.registrationFee
        );

        if (targetIndex !== -1) {
          updatedData = [...currentData];
          updatedData[targetIndex] = newData;
        } else {
          console.error(
            'Could not find original data to update:',
            originalData
          );
          updatedData = currentData; // Fallback: no change
        }
      } else {
        // Add new entry
        updatedData = [...currentData, newData];
      }

      formik.setFieldValue('adaptiveRegistrationFeeData', updatedData, false);
      handleCloseAdaptivePricingDialog();
      // TODO: Save to database
    };

    const handleDeleteAdaptivePricing = (dataToDelete) => {
      console.log('handleDeleteAdaptivePricing', dataToDelete);

      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.isConfirmed) {
          const currentData = formik.values?.adaptiveRegistrationFeeData || [];
          const updatedData = currentData.filter(
            (item) =>
              // Use the same matching criteria as the update function
              !(
                item.fromRegistrations === dataToDelete.fromRegistrations &&
                item.fromDate === dataToDelete.fromDate &&
                item.registrationFee === dataToDelete.registrationFee
              )
          );

          formik.setFieldValue(
            'adaptiveRegistrationFeeData',
            updatedData,
            false
          );
          handleCloseAdaptivePricingDialog();
          // TODO: Save to database
        }
      });
    };

    const handleDatePickerChanged = useCallback(
      async (fieldName, newValue) => {
        if (newValue.length > 0) {
          await formik.setFieldValue(fieldName, newValue[0], false);
        }
      },
      [formik]
    );

    const calculateSystemFee = useCallback(
      async (registrationFee) => {
        console.log('Calculating system fee', {
          productAmount: registrationFee,
          productType: 'registration',
          subType: event?.type,
          organizationId: event?.organization?._id,
          eventInstanceId: formik?.values?._id
        });

        let calculatedSystemFee = 0;

        if (Number(registrationFee) > 0) {
          await getSystemFeeForProductType({
            productAmount: registrationFee * 100,
            productType: 'registration',
            subType: event?.type,
            organizationId: event?.organization?._id,
            eventInstanceId: formik?.values?._id
          })?.then((res) => {
            console.log('System fee response', res);
            calculatedSystemFee = res?.systemFee / 100;
          });
        }

        console.log('Calculated system fee', calculatedSystemFee.toFixed(2));

        setCalculatedSystemFee(calculatedSystemFee.toFixed(2));
      },
      [event?.type, event?.organization?._id, formik?.values?._id]
    );

    const handleRegistrationFeeBlur = useCallback(() => {
      console.log(
        'handleRegistrationFeeBlur',

        formik.dirty
      );
      if (formik.dirty) {
        Swal.fire({
          icon: 'info',
          title: 'Registration Fee Updated',
          text: 'This field is only a default value for new divisions. Prices for existing divisions need to be changed in the division dialog below.',
          confirmButtonText: 'OK'
        });
      }
    }, [formik.dirty]);

    // Debounce system fee calculation
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        calculateSystemFee(formik.values?.registrationFee);
      }, 500);

      return () => clearTimeout(timeoutId);
    }, [formik.values?.registrationFee, calculateSystemFee]);

    const adaptiveData = formik.values?.adaptiveRegistrationFeeData || [];

    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString(i18n.language);
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: event?.currency || 'EUR'
      }).format(amount);
    };

    return (
      <Grid item xs={12} sm={12}>
        <Table>
          <TableHead></TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                width="100%"
                padding={[1, 1, 1, 0.5]}
                sx={{ fontWeight: 'bold' }}
              >
                Adaptive Registration Fee
              </TableCell>
              <TableCell
                width="100%"
                padding={[1, 1, 1, 0.5]}
                sx={{ minWidth: formMinWidth, textAlign: 'right' }}
              >
                <ArgonButton
                  variant="text"
                  color="success"
                  size="small"
                  onClick={() => handleOpenAdaptivePricingDialog()}
                >
                  Add Price Point
                </ArgonButton>
              </TableCell>
            </TableRow>

            {/* <TableRow>
              <TableCell
                width="100%"
                padding={[1, 1, 1, 0.5]}
                sx={{ fontWeight: 'bold' }}>
                1
              </TableCell>
              <TableCell>2</TableCell>
              <TableCell>3</TableCell>
            </TableRow> */}

            {adaptiveData.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center" padding={[1, 1, 1, 0.5]}>
                  <ArgonTypography variant="caption" color="text">
                    No price points defined
                  </ArgonTypography>
                </TableCell>
              </TableRow>
            )}

            {/* Display existing adaptive pricing entries */}
            {adaptiveData.length > 0 && (
              <>
                {adaptiveData.map((pricePoint, index) => {
                  let title;
                  if (
                    pricePoint.fromRegistrations !== null &&
                    pricePoint.fromRegistrations !== undefined
                  ) {
                    title = `From ${pricePoint.fromRegistrations} registration(s)`;
                    if (pricePoint.fromDate) {
                      title += ` or from ${formatDate(pricePoint.fromDate)}`;
                    }
                  } else {
                    title = `From ${formatDate(pricePoint.fromDate)}`;
                  }
                  return (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={() =>
                        handleOpenAdaptivePricingDialog(pricePoint)
                      }
                    >
                      <TableCell padding={[1, 1, 1, 0.5]}>
                        <ArgonBox
                          lineHeight={1.4}
                          // onClick={() => handleOpenDialog(field)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <ArgonTypography
                            display="block"
                            variant="caption"
                            fontWeight="bold"
                          >
                            {/* {field.required && <span style={{ color: 'red' }}> *</span>} */}
                            Tier {index + 1}
                          </ArgonTypography>
                          <ArgonTypography variant="caption" color="text">
                            {title}
                          </ArgonTypography>
                        </ArgonBox>
                      </TableCell>

                      <TableCell
                        padding={[1, 1, 1, 0.5]}
                        sx={{ width: formMinWidth, textAlign: 'right' }}
                      >
                        <ArgonBox
                          display="flex"
                          gap={2}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <ArgonTypography
                            display="block"
                            variant="caption"
                            fontWeight="bold"
                          >
                            {formatCurrency(pricePoint.registrationFee / 100)}
                          </ArgonTypography>
                          {/* <Tooltip title="Edit price point"> */}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenAdaptivePricingDialog(pricePoint);
                            }}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {/* </Tooltip> */}
                          {/* <Tooltip title="Delete price point">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAdaptivePricing(pricePoint);
                              }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton> 
                          </Tooltip>*/}
                        </ArgonBox>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>

        <SingleAdaptivePricingDialog
          open={openAdaptivePricingDialog}
          onClose={handleCloseAdaptivePricingDialog}
          onSave={handleSaveAdaptivePricing}
          onDelete={handleDeleteAdaptivePricing}
          existingData={editingPricePoint}
          currency={event?.currency || 'EUR'}
          defaultPrice={formik.values?.registrationFee || 0}
          isEdit={isEditMode}
          existingPricePoints={adaptiveData}
          event={event}
          eventInstanceId={formik?.values?._id}
        />
      </Grid>
    );
  }
);

EventInstancePricing.displayName = 'EventInstancePricing';

export default EventInstancePricing;
